import { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchPurchaseOrderStockReceiptList } from '#/api/srm/purchaseOrder/fetchStockReceiptList';
import useDetailTable from '#/hooks/table/useDetailTable';
import DetailTable from '#/modules/commonComponents/table/DetailTable';
import { buildPurchaseOrderInboundColumns } from '#/modules/srmMgmt/purchaseOrder/data/purchaseOrderInboundColumns';
import { getPurchaseOrderApiErrorMessage } from '#/modules/srmMgmt/purchaseOrder/helper/apiError';
import {
  purchaseOrderStockReceiptListQueryKey,
  purchaseOrderStockReceiptListQueryKeyPrefix,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

const PurchaseOrderInboundHistorySection = ({ purchaseOrderId }) => {
  const queryClient = useQueryClient();

  const handleMutated = useCallback(async () => {
    if (!purchaseOrderId) {
      return;
    }
    await queryClient.invalidateQueries({
      queryKey: purchaseOrderStockReceiptListQueryKeyPrefix(purchaseOrderId),
    });
  }, [purchaseOrderId, queryClient]);

  const columns = useMemo(
    () => buildPurchaseOrderInboundColumns({
      purchaseOrderId,
      onMutated: handleMutated,
    }),
    [purchaseOrderId, handleMutated],
  );

  const listQuery = useQuery({
    queryKey: purchaseOrderStockReceiptListQueryKey(purchaseOrderId, {}),
    queryFn: () => fetchPurchaseOrderStockReceiptList(purchaseOrderId, {}),
    enabled: Boolean(purchaseOrderId),
  });

  useEffect(() => {
    if (!listQuery.isError) {
      return;
    }
    toast.error(getPurchaseOrderApiErrorMessage(listQuery.error, '載入入庫歷史失敗'));
  }, [listQuery.isError, listQuery.error]);

  const rows = useMemo(
    () => (listQuery.data?.data ?? []).map((row, index) => ({
      ...row,
      _rowKey: row?.id != null && row.id !== ''
        ? `${row.id}-${index}`
        : `inbound-${index}`,
    })),
    [listQuery.data?.data],
  );

  const detailTableState = useDetailTable(rows, columns, {
    enableExpanding: false,
    getRowId: (row) => String(row._rowKey),
  });

  if (listQuery.isLoading) {
    return <p className="px-4 py-5 text-sm text-muted-foreground">載入中…</p>;
  }

  if (listQuery.isError) {
    return (
      <p className="px-4 py-5 text-sm text-destructive">
        {getPurchaseOrderApiErrorMessage(listQuery.error, '載入入庫歷史失敗')}
      </p>
    );
  }

  if (rows.length === 0) {
    return <p className="px-4 py-5 text-sm text-muted-foreground">尚無資料</p>;
  }

  return (
    <div className="detail-table-container">
      <DetailTable
        state={detailTableState}
        containerClassName="detail-table-scroll--section-embedded"
        tableClassName="detail-table--flat-lines"
      />
    </div>
  );
};

PurchaseOrderInboundHistorySection.propTypes = {
  purchaseOrderId: PropTypes.string,
};

export default PurchaseOrderInboundHistorySection;
